import { PieChart as PieChartIcon, TrendingUp } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface PieChartProps {
  title: string;
  value: string | React.ReactNode;
  trend: string;
  data: Array<{ name: string; value: number; [key: string]: string | number }>;
  nameKey?: string;
  dataKey: string;
  colors?: string[];
  footer?: string;
  valueType?: "number" | "amount" | "percentage";
  onClick?: () => void;
  active?: boolean;
}
export default function Component({
  title,
  value,
  trend,
  data,
  nameKey = "name",
  dataKey,
  colors = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#8dd1e1", "#d084d0"],
  footer,
  valueType = "number",
  onClick,
  active
}: PieChartProps) {
  const chartConfig: ChartConfig = {
    [dataKey]: {
      label: dataKey,
      color: colors[0],
    },
  };

  // Default colors for pie chart segments
  const COLORS = colors;

  return (
    <Tooltip>
      {!active && <TooltipContent>
          <p>Coming Soon</p>
      </TooltipContent>}
      <TooltipTrigger>
        <Card className={`bg-[#FCFCFC] ${active ? '' : 'opacity-25'}`} onClick={() => {
        if (onClick) {
          onClick();
        }
      }} style={{
        cursor: onClick ? "pointer" : "default"
      }}>
        <div className="m-4 flex items-center text-gray-600">
          <PieChartIcon className="w-4 h-4" />
          <p className="text-sm ml-1">{title}</p>
        </div>
        <hr className="my-4" />
        <CardHeader>
          <CardTitle>{value}</CardTitle>
          <CardDescription>
            <div className="font-medium leading-none">{trend}</div>
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ChartContainer config={chartConfig} className="h-full w-full">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={false}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey={dataKey}
                  nameKey={nameKey}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      indicator="dot"
                      labelFormatter={(label) => label}
                      formatter={(value) => {
                        if (valueType === "amount") {
                          return convertToCurrencyRounded(value as number);
                        } else if (valueType === "percentage") {
                          return `${value}%`;
                        } else {
                          return value.toString();
                        }
                      }}
                    />
                  }
                />
                <Legend 
                    align="center"
                    layout="horizontal"
                    verticalAlign="top"
                />
              </PieChart>
            </ChartContainer>
          </ResponsiveContainer>
        </CardContent>
        <CardFooter className="flex-col items-start gap-2 text-sm">
          {footer && (
            <div className="leading-none text-muted-foreground">{footer}</div>
          )}
        </CardFooter>
      </Card>
      </TooltipTrigger>
    </Tooltip>
  );
}
